<template>
  <div :class="$style.settingCategory">
    <BaseCard
      v-if="company.paymentGatewayProvider?.key === 'adyen' && unleash.isEnabled('SalonizedPayOnline')"
    >
      <div :class="$style.salonizedPayEnableBanner">
        <div>
          <BaseCheckbox
            v-model="onlinePaymentEnabled"
            :label="
              $t(
                'register.salonized_pay.dashboard.online_payments.banner_text',
                brandNamePay(),
              )
            "
            mb
            :disabled="!onlinePaymentEnabled && !isOnlinePaymentAllowed"
            v-test="'booking-settings-salonizedPayOnlineToggle'"
          />
          <BaseText
            :href="
              $t('register.salonized_pay.dashboard.online_payments.banner_link')
            "
          >
            {{ $t('global.learn_more') }}
          </BaseText>
        </div>
        <img
          src="/img/salonized-pay/logo.svg"
          :class="$style.bannerImage"
        >
      </div>
    </BaseCard>
    <BaseCheckbox
      v-else
      v-model="onlinePaymentEnabled"
      :disabled="!onlinePaymentEnabled && !isOnlinePaymentAllowed"
      :label="$t('admin.booking.online_payment.label')"
      v-test="'booking-settings-onlinePaymentToggle'"
    />
    <BaseAlert
      v-if="isFiskalySignBlocking"
      color="warning"
      :text="$t('admin.booking.online_payment.fiskaly_sign.alert')"
      :primaryAction="{
        text: $t('admin.booking.online_payment.fiskaly_sign.action'),
        routerLink: { name: 'integrations' }
      }"
      mt
      v-test="'fiskaly-sign-required-alert'"
    />
    <BaseAlert
      v-if="showPaymentInfoAlert"
      :text="$t(compliantKey('admin.booking.online_payment.payment_link_info.text'))"
      color="info"
      mt
      closable
      @close="showPaymentInfoAlert = false"
      v-test="'payment-link-info-alert'"
    />
    <BaseAlert
      v-if="company.paymentGatewayStatus === PaymentGatewayStatus.MollieError"
      color="warning"
      :text="$t('admin.booking.online_payment.mollie_error.alert')"
      mt
      v-test="'reenable-mollie-alert'"
    />
    <BaseAlert
      v-if="showRequestsDisabledByOnlinePayment"
      color="warning"
      :text="$t('admin.booking.online_payment.appointment_requests_alert')"
      :primaryAction="$t('global.actions.click_here')"
      mt
      @primaryAction="cancelOnlinePayments"
      v-test="'appointment-requests-disabled-alert'"
    />
    <BaseRadio
      v-if="onlinePaymentEnabled && canEnablePayment"
      v-model="settings.onlinePayment"
      :options="onlinePaymentOptions"
      mt
      v-test="'booking-settings-onlinePaymentOptions'"
    />
    <EnablePayment
      v-model="canEnablePayment"
      :description="$t('admin.booking.online_payment_warning')"
      mt
    />
    <div
      v-if="
        canEnablePayment &&
        (settings.onlinePayment === 'PERCENTAGE' ||
          settings.onlinePayment === 'REQUIRED')
      "
      class="mt-1"
    >
      <BaseRadio
        v-model="settings.prepaymentsForNewCustomersOnly"
        :label="$t('admin.booking.online_payment_customers.label')"
        :options="[
          {
            value: false,
            label: $t('admin.booking.online_payment_customers.options.all')
          },
          {
            value: true,
            label: $t('admin.booking.online_payment_customers.options.new')
          }
        ]"
        mb
        v-test="'booking-settings-onlinePaymentCustomers'"
      />
      <div
        v-if="canEnableOnlinePayments"
        v-test="'booking-settings-onlinePaymentRequirements'"
      >
        <BaseRadio
          v-model="appliesTo"
          :label="$t('admin.booking.online_payment.applies_to.heading')"
          :options="[
            {
              value: AppliesToKeys.All,
              label: $t('admin.booking.online_payment.applies_to.options.all')
            },
            {
              value: AppliesToKeys.Price,
              label: $t(
                'admin.booking.online_payment.applies_to.options.price.label'
              )
            },
            {
              value: AppliesToKeys.Duration,
              label: $t(
                'admin.booking.online_payment.applies_to.options.duration.label'
              )
            }
          ]"
          v-test="'booking-settings-onlinePaymentRequirement'"
        />
      </div>
      <BaseInputPricePercentage
        v-if="settings.onlinePayment === 'PERCENTAGE' && unleash.isEnabled('FixedDepositAmount')"
        v-model="depositValue"
        v-model:type="settings.onlinePaymentDepositType"
        :label="$t('admin.booking.online_payment_deposit.label')"
        :minValue="1"
        :maxPercentage="100"
        required
        v-test="'booking-settings-onlinePaymentDeposit'"
      />
      <BaseInput
        v-else-if="settings.onlinePayment === 'PERCENTAGE'"
        v-model="settings.onlinePaymentPercentage"
        :label="$t('admin.booking.online_payment_percentage.label')"
        type="number"
        unitLabel="percentage"
        :decimals="2"
        :minValue="1"
        :maxValue="100"
        mt
        v-test="'booking-settings-onlinePaymentPercentage'"
      />
      <div
        v-if="canEnableOnlinePayments"
        v-test="'booking-settings-onlinePaymentRequirements'"
      >
        <BaseInput
          v-show="appliesTo === AppliesToKeys.Price"
          v-model="props.settings.onlinePaymentMinimumPriceValue"
          :minValue="0"
          type="currency"
          :label="
            $t(
              'admin.booking.online_payment.applies_to.options.price.input_label'
            )
          "
          mt
          v-test="'booking-settings-onlinePaymentPrice'"
        />
        <BaseInput
          v-show="appliesTo === AppliesToKeys.Duration"
          v-model="props.settings.onlinePaymentMinimumDurationValue"
          :minValue="0"
          type="number"
          unitLabel="minute"
          :label="
            $t(
              'admin.booking.online_payment.applies_to.options.duration.input_label'
            )
          "
          mt
          v-test="'booking-settings-onlinePaymentDuration'"
        />
      </div>
    </div>
    <BaseInput
      v-if="onlinePaymentEnabled"
      v-model="settings.onlinePaymentCharge"
      :label="$t('admin.booking.online_payment_charge.label')"
      type="currency"
      mt
      v-test="'booking-settings-onlinePaymentCharge'"
    />
    <BaseAlert
      v-if="onlinePaymentEnabled"
      color="info"
      :text="
        $t('admin.booking.online_payment.alert', {
          amount: filters.currency(50)
        })
      "
      mt
      v-test="'online-payment-alert'"
    />
    <BaseCheckbox
      v-if="onlinePaymentEnabled && unleash.isEnabled('PayWithGiftcard')"
      v-model="settings.giftcardPaymentsAllowed"
      :label="$t('admin.booking.giftcard_payment_allowed.label')"
      mt
      v-test="'booking-settings-giftcardPaymentsAllowed'"
    />

    <BaseCheckbox
      v-if="unleash.isEnabled('SalonizedPayOnlineTipping')"
      v-model="settings.onlinePaymentTipping"
      :label="$t('admin.booking.online_payment_tipping.label')"
      mt
      v-test="'booking-settings-onlinePaymentTipping'"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import { compliantKey } from '@/helpers/compliant-register';
import { useAdyenStore } from '@/stores/adyen';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import {
  OnlinePayment,
  OnlinePaymentDepositType,
  PaymentGatewayStatus,
} from '@/types';
import unleash from '@/unleash';
import EnablePayment from '../_shared/EnablePayment.vue';
import { useBookingSettingsOptions } from './input-options';

const props = defineProps<{
  settings: any;
}>();

const { hasFeatureFlag } = useUserStore();
const { company } = storeToRefs(useCompanyStore());

const { onlinePaymentOptions } = useBookingSettingsOptions();

const canEnablePayment = ref(false);
const isFiskalySignBlocking = computed(
  () =>
    company.value.country === 'fr' &&
    unleash.isEnabled('EnableFiskalySign') &&
    hasFeatureFlag('fiskaly-sign') &&
    !hasFeatureFlag('fiskaly-sign-enabled'),
);
const isOnlinePaymentAllowed = computed(
  () => canEnablePayment.value && !isFiskalySignBlocking.value,
);
const showPaymentInfoAlert = ref(false);

const isSalonizedPayEnabled = () =>
  unleash.isEnabled('SalonizedPay') && unleash.isEnabled('SalonizedPayOnline');

const hasPaymentLink = () => company.value.settings.sales.paymentLink;

const handleSalonizedPayRedirect = (enabled: boolean) => {
  if (enabled && isSalonizedPayEnabled()) {
    redirectToSalonizedPay();
  }
};

const handlePaymentInfoAlert = (enabled: boolean) => {
  if (!enabled && hasPaymentLink()) {
    showPaymentInfoAlert.value = true;
  } else {
    showPaymentInfoAlert.value = false;
  }
};

const updateOnlinePaymentSetting = (enabled: boolean) => {
  props.settings.onlinePayment = enabled
    ? OnlinePayment.Optional
    : OnlinePayment.Disabled;
};

const onlinePaymentEnabled = computed({
  get() {
    return (
      canEnablePayment.value &&
      props.settings.onlinePayment !== OnlinePayment.Disabled
    );
  },
  set(enabled) {
    handleSalonizedPayRedirect(enabled);
    handlePaymentInfoAlert(enabled);
    updateOnlinePaymentSetting(enabled);
  },
});

const canEnableOnlinePayments = computed(
  () => hasFeatureFlag('module-marketing') || hasFeatureFlag('module-register'),
);

enum AppliesToKeys {
  All = 'onlinePaymentAll',
  Duration = 'onlinePaymentMinimumDuration',
  Price = 'onlinePaymentMinimumPrice',
}

const appliesTo = computed({
  get() {
    return Object.values(AppliesToKeys).find((key) => props.settings[key]);
  },
  set(value) {
    Object.values(AppliesToKeys).forEach((key) => {
      props.settings[key] = key === value;
    });
  },
});

const { adyenAppState } = useAdyenStore();
const router = useRouter();
const redirectToSalonizedPay = () => {
  if (
    company.value.paymentGatewayProvider?.key === 'adyen' &&
    adyenAppState.verificationStatus !== 'VALID'
  ) {
    router.push({
      name: 'salonized-pay',
      query: { redirect: 'settings' },
    });
  }
};

const showRequestsDisabledByOnlinePayment = ref(false);

const isEnablingOnlinePayment = (oldVal: string, newVal: string) =>
  oldVal === OnlinePayment.Disabled && newVal !== OnlinePayment.Disabled;

const hasConflictingAppointmentSettings = () =>
  props.settings.appointmentRequiresConfirmation;

const handleConflictingSettings = () => {
  // Disable appointment requests if online payments are enabled and inform the user
  props.settings.appointmentRequiresConfirmation = false;
  showRequestsDisabledByOnlinePayment.value = true;
};

const resetAlertState = () => {
  showRequestsDisabledByOnlinePayment.value = false;
};

watch(
  () => props.settings.onlinePayment,
  (newVal, oldVal) => {
    resetAlertState();

    if (
      isEnablingOnlinePayment(oldVal, newVal) &&
      hasConflictingAppointmentSettings()
    ) {
      handleConflictingSettings();
    }
  },
);

const cancelOnlinePayments = () => {
  showRequestsDisabledByOnlinePayment.value = false;
  props.settings.onlinePayment = OnlinePayment.Disabled;
  props.settings.appointmentRequiresConfirmation = true;
};

// The %/€ toggle converts the value it holds when switching (10% becomes €10),
// so it gets one value and we write it to the setting that matches the type
const depositValue = ref(
  props.settings.onlinePaymentDepositType ===
    OnlinePaymentDepositType.FixedAmount
    ? props.settings.onlinePaymentDepositAmount
    : props.settings.onlinePaymentPercentage,
);

watch(depositValue, (value) => {
  if (
    props.settings.onlinePaymentDepositType ===
    OnlinePaymentDepositType.FixedAmount
  ) {
    props.settings.onlinePaymentDepositAmount = value;
  } else {
    props.settings.onlinePaymentPercentage = value;
  }
});

watch(
  () => props.settings.onlinePayment,
  (value) => {
    // When the user enables online payment with percentage, and no percentage has been set, set it to 10
    // This is to discourage users from using very low values
    if (
      value === OnlinePayment.Percentage &&
      !props.settings.onlinePaymentPercentage
    ) {
      props.settings.onlinePaymentPercentage = 10;
      if (
        props.settings.onlinePaymentDepositType ===
        OnlinePaymentDepositType.Percentage
      ) {
        depositValue.value = 10;
      }
    }
  },
);
</script>

<style
  lang="scss"
  module
>
.salonizedPayEnableBanner {
  display: flex;
  gap: 0.5 * $spacing;
}

.bannerImage {
  width: 47.081px;
  height: 54px;
}

.settingCategory {
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
    padding-bottom: $spacing;
    margin-bottom: $spacing;
  }
}
</style>
